import type { StateStorage } from 'zustand/middleware';
import type { LoginLogar } from './types/LoginLogar';

export const LOGIN_EMPRESA_STORAGE_KEY = 'login-empresa';
export const EMPRESA_IDPK_ATUAL_STORAGE_KEY = 'empresaIdpkAtual';

type PersistedLogin = {
    state: {
        login: LoginLogar | null;
        sessionId?: string | null;
        empresaIdpk?: number | null;
        isFintech?: boolean;
    };
    version?: number;
};

type EmpresaDaAba = {
    usuarioIdpk: number;
    sessionId: string | null;
    empresaIdpk: number | null;
};

export function createLoginStorage(shared: Storage, tab: Storage): StateStorage {
    let lastLogin: string | undefined;

    const emptyPersistedState = (): string => {
        lastLogin = JSON.stringify({ login: null, sessionId: null });

        tab.removeItem(LOGIN_EMPRESA_STORAGE_KEY);
        shared.removeItem(EMPRESA_IDPK_ATUAL_STORAGE_KEY);

        return JSON.stringify({
            state: { empresaIdpk: null, isFintech: false, login: null, sessionId: null },
            version: 0,
        });
    };

    return {
        getItem: (name) => {
            const value = shared.getItem(name);
            if (!value) {
                return emptyPersistedState();
            }

            let persisted: PersistedLogin;
            try {
                persisted = JSON.parse(value) as PersistedLogin;
            } catch {
                shared.removeItem(name);
                return emptyPersistedState();
            }

            const { login } = persisted.state;
            const sessionId = persisted.state.sessionId ?? null;
            lastLogin = JSON.stringify({ login, sessionId });

            let empresaDaAba: EmpresaDaAba | null = null;
            try {
                empresaDaAba = JSON.parse(tab.getItem(LOGIN_EMPRESA_STORAGE_KEY) || 'null');
            } catch {
                // Uma seleção inválida não impede a recuperação do login.
            }

            const empresas = login?.usuario_empresa || [];
            const mesmaSessao =
                !!login &&
                empresaDaAba?.usuarioIdpk === login.usu_idpk &&
                empresaDaAba.sessionId === sessionId;

            // Preserva a seleção desta aba. Novas abas usam a última empresa ativa,
            // desde que ela ainda esteja entre as empresas permitidas no login.
            const selectedIdpk = mesmaSessao
                ? empresaDaAba?.empresaIdpk
                : Number(shared.getItem(EMPRESA_IDPK_ATUAL_STORAGE_KEY));
            const empresa = empresas.find((ue) => ue.use_empresa_idpk === selectedIdpk);

            if (login) {
                tab.setItem(
                    LOGIN_EMPRESA_STORAGE_KEY,
                    JSON.stringify({
                        empresaIdpk: empresa?.use_empresa_idpk ?? null,
                        sessionId,
                        usuarioIdpk: login.usu_idpk,
                    } satisfies EmpresaDaAba),
                );
            } else {
                tab.removeItem(LOGIN_EMPRESA_STORAGE_KEY);
                shared.removeItem(EMPRESA_IDPK_ATUAL_STORAGE_KEY);
            }

            return JSON.stringify({
                ...persisted,
                state: {
                    empresaIdpk: empresa?.use_empresa_idpk ?? null,
                    isFintech: empresa?.empresa[0]?.emp_sistema_tipo_uso === 'F',
                    login,
                    sessionId,
                },
            });
        },
        removeItem: (name) => {
            shared.removeItem(name);
            tab.removeItem(LOGIN_EMPRESA_STORAGE_KEY);
            shared.removeItem(EMPRESA_IDPK_ATUAL_STORAGE_KEY);
            lastLogin = JSON.stringify({ login: null, sessionId: null });
        },
        setItem: (name, value) => {
            const persisted = JSON.parse(value) as PersistedLogin;
            const { login, empresaIdpk } = persisted.state;
            const sessionId = persisted.state.sessionId ?? null;
            const serializedLogin = JSON.stringify({ login, sessionId });

            // Trocar apenas a empresa não regrava a sessão compartilhada,
            // que pode ter recebido um token atualizado em outra aba.
            if (serializedLogin !== lastLogin) {
                shared.setItem(name, JSON.stringify({ ...persisted, state: { login, sessionId } }));
                lastLogin = serializedLogin;
            }

            if (login) {
                tab.setItem(
                    LOGIN_EMPRESA_STORAGE_KEY,
                    JSON.stringify({
                        empresaIdpk: empresaIdpk ?? null,
                        sessionId,
                        usuarioIdpk: login.usu_idpk,
                    } satisfies EmpresaDaAba),
                );
            } else {
                tab.removeItem(LOGIN_EMPRESA_STORAGE_KEY);
                shared.removeItem(EMPRESA_IDPK_ATUAL_STORAGE_KEY);
            }
        },
    };
}
